/**
 * VENDOR MODULES
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { isRacingSport } from 'shared/utils/markets';
import { cn } from 'lib/utils';
import { getSportTextColorClasses } from 'shared/utils/sport-colors';
import WeekSelector from './WeekSelector';
import { useServerNow } from 'shared/queries/serverTime';
import { useEventResultsQuery } from 'features/results/queries';
import { getEventEndMsFromResults } from 'features/results/eventEndFromResults';
import { UPCOMING_EVENTS_LIST_SIZE, type Football3dWeek } from 'features/events/api';
import { getEventIdForDisplay } from 'features/events/eventId';
import type { Event } from 'features/events/types';
import { useSchedulesQuery, useScheduleUpcomingQuery } from 'features/events/queries';
import { useScheduleContext } from 'features/events/context/ScheduleContext';
import { useMobileTabsContext } from 'app/stores/mobile-tabs-store';
import { useOngoingEventQuery } from 'features/video/queries';
import { computeEventPhase } from 'features/events/hooks/useScheduleStatus';

const RACING_SPORT_IDS = new Set([10, 11, 20, 21, 30]);
const BASEBALL_SPORT_ID = 40;
const FOOTBALL3D_SPORT_ID = 50;

function formatEventTime(timestamp: number | undefined | null): string {
  if (!timestamp) return '';
  const date = new Date(timestamp * 1000);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

function getEventLabelFromEvent(event: { event_timestamp?: number } | null | undefined): string {
  const { event_timestamp } = event || {};
  return formatEventTime(event_timestamp);
}

/** Legacy type alias kept for any external imports */
export type EventStatus = {
  isLive: boolean;
  isFinished: boolean;
  isCancelled: boolean;
  countdownLabel: string | null;
};

interface EventSelectorItemProps {
  event: Record<string, unknown>;
  isLast: boolean;
  selectedEventId: unknown;
  onEventClick: (event: Record<string, unknown>) => void;
  getEventLabel?: (event: Record<string, unknown>) => string;
  disableResultsCheck: boolean;
  realtimeResults: unknown;
  /** REST-first: ongoing payload from GET /live (for LIVE/Cancelled status) */
  ongoing?: Record<string, unknown> | null;
  /** When true, show "Starts in Xm" or "Starts in Xs" for future events; when false, future events show id+time only (no countdown). */
  isNextEvent: boolean;
}

function EventSelectorItem({
  event,
  isLast,
  selectedEventId,
  onEventClick,
  getEventLabel,
  disableResultsCheck,
  realtimeResults,
  ongoing = null,
  isNextEvent = false,
}: EventSelectorItemProps) {
  const { t } = useTranslation();
  const serverNowMs = useServerNow();
  const isSelected =
    selectedEventId != null &&
    event?.event_id != null &&
    selectedEventId === event.event_id;
  const eventTimeLabel = getEventLabel ? getEventLabel(event) : getEventLabelFromEvent(event as { event_timestamp?: number });
  const nowSec = Math.floor(serverNowMs / 1000);
  const eventPhase = computeEventPhase(event, ongoing ?? null, nowSec);

  const sportObj = event?.sport as { sport_id?: number } | undefined;
  const sport_id = sportObj?.sport_id ?? event?.sport_id;
  const textColorSport = getSportTextColorClasses(sport_id as number);

  const eventStartMs = event?.event_timestamp ? Number(event.event_timestamp) * 1000 : null;
  const hasStarted =
    eventStartMs != null && Number.isFinite(eventStartMs) && serverNowMs >= eventStartMs;
  const resultsQuery = useEventResultsQuery({
    eventId: event?.event_id as number | undefined,
    scheduleId: event?.schedule_id as number | string | undefined,
    enabled: !!event?.event_id && hasStarted && !disableResultsCheck,
  });
  const effectiveResults = disableResultsCheck ? (realtimeResults ?? null) : (resultsQuery.data ?? null);
  const hasResults =
    !!effectiveResults &&
    typeof effectiveResults === 'object' &&
    Object.keys(effectiveResults as object).length > 0;
  const eventEndMs = getEventEndMsFromResults(effectiveResults as Record<string, unknown>);
  const eventHasEnded =
    eventEndMs != null && Number.isFinite(eventEndMs) && serverNowMs >= eventEndMs;
  const eventClosed =
    eventPhase.phase === 'ended' || (!disableResultsCheck && hasResults && eventHasEnded);

  const statusText =
    eventClosed
      ? (t('EVENT_FINISHED') || 'Finished')
      : eventPhase.phase === 'cancelled'
        ? (t('EVENT_CANCELLED') || 'Cancelled')
        : eventPhase.phase === 'live' || eventPhase.phase === 'starting'
          ? (t('LIVE') || 'LIVE')
          : (eventPhase.countdownLabel ?? '—');

  return (
    <button
      type="button"
      data-sport-id={sport_id}
      data-selected={isSelected}
      data-closed={eventClosed}
      aria-label={eventTimeLabel ? `Event ${eventTimeLabel}, ${event.event_id}` : `Event ${event.event_id}`}
      aria-current={isSelected ? 'true' : undefined}
      className={cn(
        'flex flex-col items-center justify-center',
        'px-5 md:px-7 py-2',
        'min-w-[90px] md:min-w-[110px]',
        'h-full',
        'rounded-none',
        'transition-colors duration-200 transition-opacity duration-200',
        'flex-shrink-0',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sport-color)] focus-visible:ring-inset',
        !isLast ? 'border-r border-border' : '',
        textColorSport
      )}
      style={{
        scrollSnapAlign: 'start',
        backgroundColor: 'var(--sport-color)',
        opacity: isSelected ? 1 : 0.7,
        ...(!isSelected && (eventPhase.phase === 'live' || eventPhase.phase === 'starting') ? { borderBottom: '3px solid rgba(255,255,255,0.5)' } : {}),
        ...(eventClosed ? { borderBottom: '3px solid transparent' } : {}),
      }}
      onClick={() => onEventClick(event)}
      title={`Event #${event.event_id}${eventTimeLabel ? ` - ${eventTimeLabel}` : ''}`}
    >
      <div className="font-semibold text-sm md:text-base truncate max-w-full whitespace-nowrap">
        #{String(event.event_id)}{eventTimeLabel ? ` · ${eventTimeLabel}` : ''}
      </div>
      <div className="text-[10px] mt-0.5 font-medium opacity-90">
        {statusText}
      </div>
    </button>
  );
}

function EventSelector() {
  const { t } = useTranslation();
  const serverNowMs = useServerNow();
  const {
    currentScheduleId: current_schedule_id,
    selectedEvent: selected_event,
    selectedWeekId,
    setSelectedEvent,
    eventId: nextEventId,
  } = useScheduleContext();
  const { switchToBets } = useMobileTabsContext();

  const { data: schedules = [] } = useSchedulesQuery();
  const currentSchedule = Array.isArray(schedules)
    ? schedules.find((s: { schedule_id: unknown }) => s.schedule_id === current_schedule_id)
    : null;
  const currentSportIdRaw = currentSchedule?.sport?.sport_id ?? (currentSchedule as { sport_id?: number })?.sport_id;
  const currentSportId = currentSportIdRaw != null ? parseInt(String(currentSportIdRaw), 10) : null;
  const isFootball3d = currentSportId === FOOTBALL3D_SPORT_ID;

  /** Event selector uses REST only; no realtime stores. */
  const isRacingSchedule = false;
  const isBaseballSchedule = false;
  const isFootball3dSchedule = false;

  const { data: scheduleUpcoming } = useScheduleUpcomingQuery({
    scheduleId: current_schedule_id ?? undefined,
    numEvents: UPCOMING_EVENTS_LIST_SIZE,
  });
  const restUpcomingEvents = React.useMemo(() => {
    if (!scheduleUpcoming || current_schedule_id == null) return [];
    if ('events' in scheduleUpcoming && Array.isArray(scheduleUpcoming.events)) return scheduleUpcoming.events;
    return [];
  }, [scheduleUpcoming, current_schedule_id]);

  const ongoingQuery = useOngoingEventQuery({
    scheduleId: current_schedule_id ?? undefined,
    enabled: current_schedule_id != null,
  });

  /**
   * Track all ongoing events we've seen for this schedule so finished events
   * stay in the selector instead of vanishing when the API drops them.
   */
  const seenOngoingRef = React.useRef<{ scheduleId: string; events: Map<string, Record<string, unknown>> }>({ scheduleId: '', events: new Map() });

  React.useEffect(() => {
    const sid = current_schedule_id != null ? String(current_schedule_id) : '';
    if (seenOngoingRef.current.scheduleId !== sid) {
      seenOngoingRef.current = { scheduleId: sid, events: new Map() };
    }
    const live = ongoingQuery.data;
    if (live?.event_id != null && sid) {
      const eid = String(live.event_id);
      if (!seenOngoingRef.current.events.has(eid)) {
        seenOngoingRef.current.events.set(eid, { ...live, schedule_id: current_schedule_id } as Record<string, unknown>);
      }
    }
  }, [ongoingQuery.data, current_schedule_id]);

  /** Merge REST upcoming events with all ongoing events we've observed so finished ones remain visible. */
  const restEventsWithOngoingInserted = React.useMemo(() => {
    const list = Array.isArray(restUpcomingEvents) ? [...restUpcomingEvents] : [];
    const live = ongoingQuery.data;
    const sid = current_schedule_id != null ? String(current_schedule_id) : '';

    const insertInOrder = (asEvent: any, eventTs: number) => {
      const eid = getEventIdForDisplay(asEvent);
      if (list.some((e: any) => getEventIdForDisplay(e) === eid)) return;
      const idx = list.findIndex((e: any) => e?.event_timestamp != null && Number(e.event_timestamp) > eventTs);
      if (idx < 0) list.push(asEvent);
      else list.splice(idx, 0, asEvent);
    };

    if (seenOngoingRef.current.scheduleId === sid) {
      for (const [eid, ev] of seenOngoingRef.current.events) {
        const ts = ev.event_timestamp;
        if (ts != null && Number.isFinite(Number(ts))) {
          insertInOrder(ev, Number(ts));
        }
      }
    }

    if (live?.event_id && current_schedule_id != null) {
      const ts = live.event_timestamp;
      if (ts != null && Number.isFinite(Number(ts))) {
        const asEvent = { ...live, schedule_id: current_schedule_id } as Record<string, unknown>;
        insertInOrder(asEvent, Number(ts));
      }
    }

    return list;
  }, [restUpcomingEvents, ongoingQuery.data, current_schedule_id]);

  const footballEventsForWeek = React.useMemo((): Record<string, unknown>[] => {
    if (!scheduleUpcoming || typeof scheduleUpcoming !== 'object' || !('weeks' in scheduleUpcoming)) return [];
    const weeks = (scheduleUpcoming as { weeks?: Football3dWeek[] }).weeks ?? [];
    const weekObj = selectedWeekId != null ? weeks.find(w => w.week_id === selectedWeekId) : null;
    const weekList: Record<string, unknown>[] = weekObj?.events ? [...weekObj.events] as Record<string, unknown>[] : [];
    const live = ongoingQuery.data;
    if (live?.event_id != null && live?.week_id != null && Number(live.week_id) === Number(selectedWeekId) && current_schedule_id != null) {
      const ongoingId = String(live.event_id);
      if (!weekList.some((e: { event_id?: unknown }) => getEventIdForDisplay(e as { event_id?: number | string }) === ongoingId)) {
        const ts = live.event_timestamp;
        if (ts != null && Number.isFinite(Number(ts))) {
          const liveTs = Number(ts);
          const asEvent = { ...live, schedule_id: current_schedule_id } as Record<string, unknown>;
          const idx = weekList.findIndex((e: { event_timestamp?: number }) => e?.event_timestamp != null && Number(e.event_timestamp) > liveTs);
          if (idx < 0) return [...weekList, asEvent];
          return [...weekList.slice(0, idx), asEvent, ...weekList.slice(idx)];
        }
      }
    }
    return weekList;
  }, [scheduleUpcoming, selectedWeekId, ongoingQuery.data, current_schedule_id]);

  const currentEvents: Record<string, unknown>[] =
    isFootball3d && current_schedule_id != null
      ? footballEventsForWeek
      : restEventsWithOngoingInserted;

  const sortedEvents = React.useMemo(
    () =>
      [...(Array.isArray(currentEvents) ? currentEvents : [])]
        .filter((e) => (e as any)?.event_timestamp != null)
        .sort((a, b) => Number((a as any).event_timestamp) - Number((b as any).event_timestamp)),
    [currentEvents]
  );

  const handleEventClick = React.useCallback(
    (event: Record<string, unknown>) => {
      setSelectedEvent(event as Event | null);
      switchToBets();
    },
    [setSelectedEvent, switchToBets]
  );

  const getEventLabel = React.useCallback((event: Record<string, unknown>) => {
    const { event_timestamp } = event || {};
    return formatEventTime(event_timestamp as number);
  }, []);

  const hoverStyles = React.useMemo(
    () => `
      .event-navigator div::-webkit-scrollbar {
        display: none;
      }
      .event-navigator button:hover:not([data-selected="true"]) {
        opacity: 1 !important;
      }
    `,
    []
  );

  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const rafRef = React.useRef<number | null>(null);
  const scrollDirRef = React.useRef(0);
  const [hasOverflow, setHasOverflow] = React.useState(false);

  const checkOverflow = React.useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setHasOverflow(el.scrollWidth > el.clientWidth);
  }, []);

  React.useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    checkOverflow();
    const ro = new ResizeObserver(checkOverflow);
    ro.observe(el);
    return () => ro.disconnect();
  }, [checkOverflow, sortedEvents.length]);

  const stopHoverScroll = React.useCallback(() => {
    scrollDirRef.current = 0;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const startHoverScroll = React.useCallback((dir: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    scrollDirRef.current = dir;
    if (rafRef.current) return;

    const tick = () => {
      const node = scrollerRef.current;
      const d = scrollDirRef.current;
      if (!node || !d) {
        rafRef.current = null;
        return;
      }
      node.scrollLeft += d * 9;
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  React.useEffect(() => stopHoverScroll, [stopHoverScroll]);

  const shouldScrollRef = React.useRef(false);

  React.useEffect(() => {
    shouldScrollRef.current = true;
  }, [current_schedule_id]);

  React.useEffect(() => {
    if (!shouldScrollRef.current) return;
    if (selected_event?.event_id == null) return;
    if (sortedEvents.length === 0) return;
    requestAnimationFrame(() => {
      const el = scrollerRef.current;
      if (!el) return;
      const btn = el.querySelector('[data-selected="true"]');
      if (btn) {
        shouldScrollRef.current = false;
        btn.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
      }
    });
  }, [selected_event?.event_id, sortedEvents.length]);

  if (isFootball3d) {
    return <WeekSelector />;
  }

  if (sortedEvents.length === 0) {
    return null;
  }

  const eventsWithId = sortedEvents.filter((e) => (e as any)?.event_id != null);

  if (eventsWithId.length === 0) {
    return null;
  }

  return (
    <div
      className="event-navigator relative w-full z-40 bg-card/95 supports-[backdrop-filter]:bg-card/80 backdrop-blur border-b border-border"
      style={{ height: '56px' }}
    >
      {hasOverflow && (
        <div className="hidden [@media(hover:hover)]:flex absolute inset-y-0 left-1 items-center z-50">
          <button
            type="button"
            className="px-2 py-1 rounded-md text-xs font-bold bg-card/80 supports-[backdrop-filter]:bg-card/60 backdrop-blur border border-border shadow-sm hover:bg-muted/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sport-color)] focus-visible:ring-offset-2"
            aria-label="Scroll events left"
            title="Scroll left"
            onPointerEnter={() => startHoverScroll(-1)}
            onPointerLeave={stopHoverScroll}
            onPointerDown={stopHoverScroll}
            onClick={() => scrollerRef.current?.scrollBy?.({ left: -240, behavior: 'smooth' })}
          >
            {'<<'}
          </button>
        </div>
      )}
      {hasOverflow && (
        <div className="hidden [@media(hover:hover)]:flex absolute inset-y-0 right-1 items-center z-50">
          <button
            type="button"
            className="px-2 py-1 rounded-md text-xs font-bold bg-card/80 supports-[backdrop-filter]:bg-card/60 backdrop-blur border border-border shadow-sm hover:bg-muted/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sport-color)] focus-visible:ring-offset-2"
            aria-label="Scroll events right"
            title="Scroll right"
            onPointerEnter={() => startHoverScroll(1)}
            onPointerLeave={stopHoverScroll}
            onPointerDown={stopHoverScroll}
            onClick={() => scrollerRef.current?.scrollBy?.({ left: 240, behavior: 'smooth' })}
          >
            {'>>'}
          </button>
        </div>
      )}

      <div
        ref={scrollerRef}
        className={cn(
          'overflow-y-hidden scroll-smooth h-full',
          hasOverflow ? 'overflow-x-auto' : 'overflow-x-hidden'
        )}
        style={{
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          direction: 'ltr',
          scrollPaddingLeft: '0px',
          scrollPaddingRight: '0px',
          overflowY: 'hidden',
        }}
      >
        <style>{hoverStyles}</style>
        <div
          className="flex items-center h-full"
          style={{ paddingLeft: '0px', paddingRight: '0px', minWidth: 'max-content' }}
        >
          {eventsWithId.length === 0 ? (
            <div className="px-4 py-2 text-sm text-muted-foreground" role="status">
              {t('NO_EVENTS') || 'No events'}
            </div>
          ) : (
            eventsWithId.map((event: Record<string, unknown>, index: number) => {
              const isOngoingEventFromLive =
                event?.event_id != null &&
                ongoingQuery.data?.event_id != null &&
                String(event.event_id) === String(ongoingQuery.data.event_id);
              const ongoingForStatus: Record<string, unknown> | null =
                isOngoingEventFromLive && ongoingQuery.data
                  ? {
                      event_id: ongoingQuery.data.event_id,
                      cancelled: ongoingQuery.data.cancelled === true,
                    }
                  : null;

              return (
              <EventSelectorItem
                key={`${event.event_id}-${index}`}
                event={event}
                isLast={index === eventsWithId.length - 1}
                selectedEventId={selected_event?.event_id}
                onEventClick={handleEventClick}
                getEventLabel={getEventLabel}
                disableResultsCheck={false}
                realtimeResults={null}
                ongoing={ongoingForStatus}
                isNextEvent={event?.event_id != null && String(event.event_id) === String(nextEventId)}
              />
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default EventSelector;
